import Link from "next/link";
import type { LucideIcon } from "lucide-react";
import { CircleAlert, SearchX } from "lucide-react";

import { cn } from "@/lib/utils";

import Button from "./Button";

/**
 * Estados da lista (tela-modelo 1h): vazio, carregando e erro. Mesmo
 * contêiner nos três — fundo da página, régua de 2px em cima (vermelha no
 * erro) — para a troca de estado não mexer no layout.
 *
 * Ação com `onClick` só em componente cliente; em componente de servidor use
 * `href`.
 */

type Action = {
  label: string;
  onClick?: () => void;
  href?: string;
  /** Desligada enquanto a ação grava (ex.: "Adicionando…"). */
  disabled?: boolean;
};

function ActionButton({
  action,
  variant,
}: {
  action: Action;
  variant: "outline" | "rail";
}) {
  if (action.href) {
    return (
      <Button asChild variant={variant}>
        <Link href={action.href}>{action.label}</Link>
      </Button>
    );
  }
  return (
    <Button
      variant={variant}
      onClick={action.onClick}
      disabled={action.disabled}
    >
      {action.label}
    </Button>
  );
}

/**
 * Nenhum resultado: ícone + título + 1 linha + ação ("Limpar filtros").
 * `secondaryAction`: uma segunda saída em link ao lado da ação (ex.: os
 * contatos do cliente — "Adicionar cliente como contato" e, em link,
 * "Adicionar outro contato").
 */
export function EmptyState({
  title,
  text,
  icon: Icon = SearchX,
  action,
  secondaryAction,
  className,
}: {
  title: string;
  text?: React.ReactNode;
  icon?: LucideIcon;
  action?: Action;
  secondaryAction?: Action;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex flex-col items-center gap-2.5 border border-t-2 border-line border-t-ink bg-ground px-7 py-10 text-center",
        className,
      )}
    >
      <Icon className="size-7 text-ink-3" aria-hidden />
      <p className="text-lg font-extrabold tracking-caps text-ink uppercase">
        {title}
      </p>
      {text && <p className="max-w-md text-sm text-ink-2">{text}</p>}
      {(action || secondaryAction) && (
        <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          {action && <ActionButton action={action} variant="outline" />}
          {secondaryAction &&
            (secondaryAction.href ? (
              <Link
                href={secondaryAction.href}
                className="text-ui font-semibold text-action hover:underline"
              >
                {secondaryAction.label}
              </Link>
            ) : (
              <Button
                variant="link"
                size="inline"
                onClick={secondaryAction.onClick}
                disabled={secondaryAction.disabled}
                className="pointer-coarse:min-h-11"
              >
                {secondaryAction.label}
              </Button>
            ))}
        </div>
      )}
    </div>
  );
}

/** Falha ao carregar: régua vermelha, "Tentar de novo" + reportar. */
export function ErrorState({
  title = "Não foi possível carregar",
  text = "Verifique a conexão e tente de novo. Se continuar, avise o suporte.",
  onRetry,
  report,
  className,
}: {
  title?: string;
  text?: React.ReactNode;
  onRetry?: () => void;
  /** Link "Reportar problema" (canal de ajuda existente). */
  report?: Action;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center gap-2.5 border border-t-2 border-line border-t-alert bg-ground px-7 py-10 text-center",
        className,
      )}
    >
      <CircleAlert className="size-7 text-alert-ink" aria-hidden />
      <p className="text-lg font-extrabold tracking-caps text-ink uppercase">
        {title}
      </p>
      {text && <p className="max-w-md text-sm text-ink-2">{text}</p>}
      {(onRetry || report) && (
        <div className="mt-1.5 flex flex-wrap items-center justify-center gap-4">
          {onRetry && (
            <ActionButton
              action={{ label: "Tentar de novo", onClick: onRetry }}
              variant="rail"
            />
          )}
          {report &&
            (report.href ? (
              <Link
                href={report.href}
                className="text-ui font-semibold text-action hover:underline"
              >
                {report.label}
              </Link>
            ) : (
              <button
                type="button"
                onClick={report.onClick}
                className="text-ui font-semibold text-action hover:underline"
              >
                {report.label}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

/** Carregando: linhas de 58px com barras no lugar do texto. */
export function TableSkeleton({
  rows = 6,
  columns = 5,
  className,
}: {
  rows?: number;
  columns?: number;
  className?: string;
}) {
  return (
    <div
      role="status"
      aria-label="Carregando"
      className={cn("border-t-2 border-ink", className)}
    >
      <div className="flex h-10 items-center gap-6 border-b border-line px-5">
        {Array.from({ length: columns }).map((_, c) => (
          <span
            key={c}
            className="h-2 flex-1 animate-pulse bg-line"
          />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="flex min-h-14.5 items-center gap-6 border-b border-line px-5"
        >
          {Array.from({ length: columns }).map((_, c) => (
            <span
              key={c}
              className={cn(
                "h-2.5 animate-pulse",
                c === 0 ? "flex-2 bg-line" : "flex-1 bg-panel",
              )}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
